import { supabase } from "./supabase";
import type { TableName } from "../modules/content/contentConfig";

type SchemaTable = TableName | "blog_posts" | "stores_directory" | "leads" | "directory_invitations";

const baseColumns: Record<SchemaTable, string[]> = {
  pages: [
    "id",
    "slug",
    "title",
    "content",
    "seo_title",
    "seo_description",
    "og_image_url",
    "is_published",
    "published_at",
    "created_at",
    "updated_at",
  ],
  services: [
    "id",
    "slug",
    "name",
    "summary",
    "description",
    "icon",
    "sort_order",
    "is_published",
    "created_at",
    "updated_at",
  ],
  plans: [
    "id",
    "slug",
    "name",
    "price_amount",
    "price_currency",
    "billing_period",
    "features",
    "is_featured",
    "sort_order",
    "is_published",
    "created_at",
    "updated_at",
  ],
  portfolio_items: [
    "id",
    "slug",
    "title",
    "summary",
    "cover_image_url",
    "gallery",
    "client_name",
    "sort_order",
    "is_published",
    "created_at",
    "updated_at",
  ],
  case_studies: [
    "id",
    "slug",
    "title",
    "summary",
    "content",
    "results",
    "cover_image_url",
    "is_published",
    "published_at",
    "created_at",
    "updated_at",
  ],
  blog_categories: ["id", "slug", "name"],
  blog_posts: [
    "id",
    "slug",
    "title",
    "excerpt",
    "content",
    "cover_image_url",
    "category_id",
    "seo_title",
    "seo_description",
    "author_id",
    "is_published",
    "published_at",
    "created_at",
    "updated_at",
  ],
  faqs: ["id", "question", "answer", "page_slug", "sort_order", "is_published"],
  media: ["id", "storage_path", "alt_text", "width", "height", "uploaded_by", "created_at"],
  stores_directory: [
    "id",
    "saas_store_id",
    "slug",
    "name",
    "description",
    "logo_url",
    "cover_image_url",
    "city",
    "category",
    "store_url",
    "is_visible_in_directory",
    "is_force_hidden",
    "saas_updated_at",
    "synced_at",
    "created_at",
  ],
  leads: [
    "id",
    "name",
    "company",
    "email",
    "phone",
    "service",
    "message",
    "source_page",
    "status",
    "created_at",
  ],
  directory_invitations: [
    "id",
    "token_hash",
    "token_prefix",
    "label",
    "single_use",
    "status",
    "expires_at",
    "revoked_at",
    "used_count",
    "last_used_at",
    "submission_id",
    "created_by",
    "created_at",
  ],
};

const enhancedColumns: Partial<Record<SchemaTable, string[]>> = {
  pages: ["canonical_url", "hero_image_url", "page_date"],
  services: [
    "category",
    "long_description",
    "benefits",
    "image_url",
    "cta_label",
    "cta_url",
    "seo_title",
    "seo_description",
  ],
  plans: [
    "offer_type",
    "summary",
    "target_audience",
    "delivery_time",
    "renewal_terms",
    "directory_priority",
    "cta_label",
    "cta_url",
    "seo_title",
    "seo_description",
  ],
  portfolio_items: [
    "category",
    "description",
    "features",
    "technologies",
    "project_url",
    "case_url",
    "is_featured",
    "seo_title",
    "seo_description",
  ],
  case_studies: [
    "portfolio_item_id",
    "subtitle",
    "hero_image_url",
    "problem",
    "objective",
    "solution",
    "result_text",
    "process",
    "technologies",
    "gallery",
    "cta_label",
    "cta_url",
    "project_url",
  ],
  faqs: ["category"],
  media: [
    "file_name",
    "mime_type",
    "size_bytes",
    "public_url",
    "associated_table",
    "associated_id",
    "metadata",
  ],
  stores_directory: [
    "is_featured",
    "sort_order",
    "editorial_status",
    "origin",
    "department",
    "province",
    "district",
    "address",
    "phone",
    "whatsapp",
    "website_url",
    "social_links",
    "gallery",
    "google_maps_url",
    "latitude",
    "longitude",
    "updated_at",
  ],
  leads: ["budget"],
  directory_invitations: [
    "mode",
    "target_store_id",
    "features",
    "billing_plan_id",
    "billing_plan_name",
    "billing_plan_price",
    "billing_plan_period",
    "billing_plan_snapshot",
    "billing_price_note",
    "billing_is_comped",
    "billing_setup_amount",
    "billing_setup_due_date",
    "billing_monthly_amount",
    "billing_yearly_amount",
    "billing_start_date",
    "billing_service_amount",
  ],
};

const columnCache = new Map<string, Set<string>>();

function isMissingColumnError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const message = "message" in error ? String(error.message) : "";
  const code = "code" in error ? String(error.code) : "";
  return code === "42703" || message.includes("does not exist") || message.includes("Could not find");
}

async function probeColumn(table: string, column: string): Promise<boolean> {
  const { error } = await supabase
    .from(table as never)
    .select(column)
    .limit(1);
  if (!error) return true;
  if (isMissingColumnError(error)) return false;
  return true;
}

export async function getSupportedColumns(
  table: SchemaTable,
): Promise<Set<string>> {
  const cached = columnCache.get(table);
  if (cached) return cached;

  const columns = new Set(baseColumns[table]);
  const optionalColumns = enhancedColumns[table] ?? [];
  const checks = await Promise.all(
    optionalColumns.map(async (column) => [column, await probeColumn(table, column)] as const),
  );

  for (const [column, exists] of checks) {
    if (exists) columns.add(column);
  }

  columnCache.set(table, columns);
  return columns;
}

export function getBaseColumns(table: SchemaTable): string[] {
  return baseColumns[table];
}

export function clearSchemaCache() {
  columnCache.clear();
}
